<script lang="ts">
	import { footerNavigation, socialLinks } from '$lib/content/data/navigation';
	import { validateEmail, MAX_EMAIL_LENGTH } from '$lib/utils/formValidation';
	import { submitNewsletter } from '$lib/api/formSubmissions';
	import { trackNewsletterSignup } from '$lib/utils/analytics';
	import type { FormState } from '$lib/types';

	let email = $state('');
	let formState = $state<FormState>('idle');
	let errorMessage = $state('');
	// Honeypot field (hidden from users, traps bots)
	let honeypot = $state('');

	async function handleSubmit(e: Event) {
		e.preventDefault();
		errorMessage = '';

		// Check honeypot - if filled, it's a bot
		if (honeypot) {
			formState = 'success'; // Fake success for bots
			return;
		}

		// Validate email
		if (!email) {
			errorMessage = 'Please enter your email address';
			return;
		}

		if (!validateEmail(email)) {
			errorMessage = 'Please enter a valid email address';
			return;
		}

		formState = 'submitting';

		try {
			const result = await submitNewsletter({ email, honeypot });
			if (result.success) {
				trackNewsletterSignup('footer');
				formState = 'success';
				email = '';
			} else {
				formState = 'error';
				errorMessage = result.message || 'Something went wrong. Please try again.';
			}
		} catch (err) {
			formState = 'error';
			errorMessage = 'Network error. Please try again later.';
		}
	}
</script>

<footer class="bg-ak-surface border-t border-ak-border-subtle">
	<div class="container mx-auto px-4 py-12 lg:py-16">
		<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-12">
			<!-- Brand Column -->
			<div class="lg:col-span-2">
				<a href="/" class="flex items-center gap-2 mb-4">
					<img src="/brand/logo-dark.webp" alt="Akonia Codex home" width="700" height="130" class="h-8 w-auto" />
				</a>
				<p class="text-sm text-ak-text-secondary mb-4">
					Helping developers build better software with practical methodology and hands-on practice.
				</p>
				<!-- Social Links -->
				<div class="flex items-center gap-4">
					{#each socialLinks as link}
						<a
							href={link.href}
							target="_blank"
							rel="noopener noreferrer"
							class="w-11 h-11 rounded-lg bg-ak-surface-overlay flex items-center justify-center text-ak-text-secondary hover:text-ak-wintergreen hover:bg-ak-wintergreen-dim transition-colors"
							aria-label={link.label}
						>
							{#if link.icon === 'linkedin'}
								<svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
									<path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
								</svg>
							{:else if link.icon === 'twitter'}
								<svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
									<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
								</svg>
							{:else if link.icon === 'youtube'}
								<svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
									<path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0-2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
								</svg>
							{:else if link.icon === 'instagram'}
									<svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
										<path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
							</svg>
						{/if}
					</a>
					{/each}
				</div>
			</div>

			<!-- Services Column -->
			<div>
				<h3 class="font-display font-semibold text-sm mb-4 uppercase tracking-wider text-ak-text-muted">Services</h3>
				<ul class="space-y-3">
					{#each footerNavigation.services as link}
						<li>
							<a href={link.href} class="inline-block py-2.5 md:py-1 min-h-11 md:min-h-0 text-sm text-ak-text-secondary hover:text-ak-wintergreen transition-colors">
								{link.label}
							</a>
						</li>
					{/each}
				</ul>
			</div>

			<!-- Company Column -->
			<div>
				<h3 class="font-display font-semibold text-sm mb-4 uppercase tracking-wider text-ak-text-muted">Company</h3>
				<ul class="space-y-3">
					{#each footerNavigation.company as link}
						<li>
							<a href={link.href} class="inline-block py-2.5 md:py-1 min-h-11 md:min-h-0 text-sm text-ak-text-secondary hover:text-ak-wintergreen transition-colors">
								{link.label}
							</a>
						</li>
					{/each}
				</ul>
			</div>

			<!-- Legal Column -->
			<div>
				<h3 class="font-display font-semibold text-sm mb-4 uppercase tracking-wider text-ak-text-muted">Legal</h3>
				<ul class="space-y-3">
					{#each footerNavigation.legal as link}
						<li>
							<a href={link.href} class="inline-block py-2.5 md:py-1 min-h-11 md:min-h-0 text-sm text-ak-text-secondary hover:text-ak-wintergreen transition-colors">
								{link.label}
							</a>
						</li>
					{/each}
				</ul>
			</div>

			<!-- Newsletter Column -->
			<div>
				<h3 class="font-display font-semibold text-sm mb-4 uppercase tracking-wider text-ak-text-muted">Newsletter</h3>
				<p class="text-sm text-ak-text-secondary mb-4">
					Get development tips delivered to your inbox.
				</p>
				{#if formState === 'success'}
					<div class="flex items-center gap-2 text-ak-wintergreen">
						<svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
							<path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
						</svg>
						<span class="text-sm">Thanks for subscribing!</span>
					</div>
				{:else}
					<form onsubmit={handleSubmit} class="flex flex-col gap-3">
						<div class="w-full">
							<input
								type="email"
								bind:value={email}
								placeholder="you@example.com"
								maxlength={MAX_EMAIL_LENGTH}
								class="w-full min-h-12 px-4 py-3 bg-ak-surface-overlay border {errorMessage ? 'border-ak-error' : 'border-ak-border-subtle'} rounded-lg text-sm focus:border-ak-wintergreen focus:outline-none transition-colors"
								disabled={formState === 'submitting'}
							/>
							{#if errorMessage}
								<p class="mt-1 text-xs text-ak-error">{errorMessage}</p>
							{/if}
						</div>
						<button
							type="submit"
							disabled={formState === 'submitting'}
							class="w-full min-h-12 px-4 py-3 bg-ak-wintergreen text-ak-deep font-medium text-sm rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
						>
							{#if formState === 'submitting'}
								<svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
									<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
									<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
								</svg>
							{:else}
								Subscribe
							{/if}
						</button>
						<!-- Honeypot field (hidden from users) -->
						<div class="hidden" aria-hidden="true">
							<label for="footer-website">Website</label>
							<input type="text" id="footer-website" bind:value={honeypot} tabindex="-1" autocomplete="off" />
						</div>
					</form>
				{/if}
			</div>
		</div>

		<!-- Bottom Bar -->
		<div class="mt-12 pt-8 border-t border-ak-border-subtle flex flex-col sm:flex-row items-center justify-between gap-4">
			<p class="text-sm text-ak-text-muted">
				© {new Date().getFullYear()} Akonia Codex. All rights reserved.
			</p>
		</div>
	</div>
</footer>
